import { AlgorithmResult, OptimizationResults } from '../types';
import { RESULT_ALGORITHM_KEYS } from './algorithmRegistry';

/**
 * Normalizes values to [0, 1] range using min-max normalization
 * @param value - Value to normalize
 * @param min - Minimum value in the range
 * @param max - Maximum value in the range
 * @returns Normalized value in [0, 1], or NaN if inputs are invalid
 */
export function minMaxNormalize(value: number, min: number, max: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(max)) return NaN;
  if (max === min) return 0.5; // Avoid division by zero
  const n = (value - min) / (max - min);
  // Clamp to [0, 1] to handle cases where value is slightly outside [min, max] range
  // This prevents normalization from producing values outside the expected bounds
  return Math.max(0, Math.min(1, n));
}

/**
 * Finds min and max values across all algorithms and all runs for a specific metric
 * @param allRuns - Array of results from all runs (each run contains results for all algorithms)
 * @param metricExtractor - Function to extract the metric value from AlgorithmResult
 * @returns Object with min, max values and hasValid flag
 *         If no valid values found, returns {min: NaN, max: NaN, hasValid: false}
 *         This prevents corrupting results with fake {min:0, max:1} ranges
 */
export function findGlobalMinMax(
  allRuns: OptimizationResults[],
  metricExtractor: (result: AlgorithmResult) => number
): { min: number; max: number; hasValid: boolean } {
  let min = Infinity;
  let max = -Infinity;
  let hasValid = false;

  const algorithmKeys = RESULT_ALGORITHM_KEYS;

  for (const run of allRuns) {
    for (const key of algorithmKeys) {
      const result = run[key];
      if (!result) continue;

      const value = metricExtractor(result);
      if (Number.isFinite(value)) {
        hasValid = true;
        min = Math.min(min, value);
        max = Math.max(max, value);
      }
    }
  }

  if (!hasValid) return { min: NaN, max: NaN, hasValid: false };
  return { min, max, hasValid: true };
}

/**
 * Normalizes fitness, energy, cost, responseTime, and executionTime across all algorithms and all runs
 * Energy, cost, responseTime, and executionTime are inverted (since they are minimization objectives)
 * 
 * IMPORTANT: This function normalizes across all runs passed to it. In complete runs,
 * this should be called separately for each task size to ensure normalization is done
 * per task size, not across all task sizes.
 * 
 * @param allRuns - Array of results from all runs (should be runs for a single task size)
 * @returns Normalization ranges and normalized values per run per algorithm
 */
export interface NormalizedResults {
  ranges: {
    fitness: { min: number; max: number; hasValid: boolean };
    energy: { min: number; max: number; hasValid: boolean };
    cost: { min: number; max: number; hasValid: boolean };
    responseTime: { min: number; max: number; hasValid: boolean };
    executionTime: { min: number; max: number; hasValid: boolean };
  };
  normalized: Array<{
    algorithmKey: string;
    runId: number;
    fitnessNorm: number;
    energyNorm: number;  // Already inverted (1 - normalized)
    costNorm: number;    // Already inverted (1 - normalized)
    responseTimeNorm: number;  // Already inverted (1 - normalized)
    executionTimeNorm: number; // Already inverted (1 - normalized), may be NaN if not available
  }>;
}

export function normalizeAcrossAlgorithms(
  allRuns: OptimizationResults[]
): NormalizedResults {
  const algorithmKeys = RESULT_ALGORITHM_KEYS;

  // Find global min/max for each metric
  const fitnessRange = findGlobalMinMax(allRuns, (r) => r.fitness);
  const energyRange = findGlobalMinMax(allRuns, (r) => r.energy);
  const costRange = findGlobalMinMax(allRuns, (r) => r.cost);
  const responseTimeRange = findGlobalMinMax(allRuns, (r) => r.responseTime);
  // CRITICAL: Do NOT convert missing executionTime to 0 - it corrupts min/max range calculation.
  // Using 0 would make the range artificially low, skewing normalization for all other values.
  // NaN is properly filtered out by findGlobalMinMax's isFinite check, preserving correct range.
  const executionTimeRange = findGlobalMinMax(allRuns, (r) => r.executionTime ?? NaN);

  // Normalize all values
  const normalized: NormalizedResults['normalized'] = [];

  allRuns.forEach((run, runId) => {
    algorithmKeys.forEach((algorithmKey) => {
      const result = run[algorithmKey];
      if (result) {
        // Runtime guard: Check if all required metrics are finite
        const fitness = result.fitness;
        const energy = result.energy;
        const cost = result.cost;
        const responseTime = result.responseTime;
        // CRITICAL: Do NOT convert missing executionTime to 0 - it corrupts normalization.
        // Using 0 would be treated as a valid value and included in min/max calculations,
        // artificially lowering the range and skewing all normalized values.
        // NaN is the correct representation for missing optional data.
        const executionTime = result.executionTime ?? NaN;

        // Skip this record if any required metric is not finite or if ranges are invalid
        if (!Number.isFinite(fitness) || !Number.isFinite(energy) || !Number.isFinite(cost) || !Number.isFinite(responseTime)) {
          return; // Skip this record
        }

        // Skip if required ranges are invalid (no valid data found)
        if (!fitnessRange.hasValid || !energyRange.hasValid || !costRange.hasValid || !responseTimeRange.hasValid) {
          return; // Skip this record - cannot normalize without valid ranges
        }

        // All required metrics are finite at this point, proceed with normalization
        // Normalize fitness (maximize - higher is better, so no inversion)
        const fitnessNorm = minMaxNormalize(fitness, fitnessRange.min, fitnessRange.max);

        // Normalize energy (minimize - lower is better, so invert)
        const energyNormRaw = minMaxNormalize(energy, energyRange.min, energyRange.max);
        const energyNorm = Number.isFinite(energyNormRaw) ? 1 - energyNormRaw : NaN; // Invert: lower energy = higher normalized value

        // Normalize cost (minimize - lower is better, so invert)
        const costNormRaw = minMaxNormalize(cost, costRange.min, costRange.max);
        const costNorm = Number.isFinite(costNormRaw) ? 1 - costNormRaw : NaN; // Invert: lower cost = higher normalized value

        // Normalize responseTime (minimize - lower is better, so invert)
        const responseTimeNormRaw = minMaxNormalize(responseTime, responseTimeRange.min, responseTimeRange.max);
        const responseTimeNorm = Number.isFinite(responseTimeNormRaw) ? 1 - responseTimeNormRaw : NaN; // Invert: lower responseTime = higher normalized value

        // Normalize executionTime (minimize - lower is better, so invert)
        // executionTime is optional - if NaN, keep as NaN (or use 0.5 as neutral value if needed by composite score logic)
        // Note: Composite score logic should either exclude NaN metrics or replace with neutral 0.5
        const executionTimeNormRaw = Number.isFinite(executionTime) && executionTime > 0 && executionTimeRange.hasValid
          ? minMaxNormalize(executionTime, executionTimeRange.min, executionTimeRange.max)
          : NaN;
        const executionTimeNorm = Number.isFinite(executionTimeNormRaw) ? 1 - executionTimeNormRaw : NaN; // Invert: lower executionTime = higher normalized value

        // Push normalized values (all required metrics are guaranteed to be finite at this point)
        normalized.push({
          algorithmKey,
          runId,
          fitnessNorm,
          energyNorm,
          costNorm,
          responseTimeNorm,
          executionTimeNorm, // May be NaN if executionTime was not available
        });
      }
    });
  });

  return {
    ranges: {
      fitness: fitnessRange,
      energy: energyRange,
      cost: costRange,
      responseTime: responseTimeRange,
      executionTime: executionTimeRange,
    },
    normalized,
  };
}
